import React from 'react';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { useChanges, Form } from '../../../../components/Form/Form';
import { handleError } from '../../../../model/notification';
import { Datepicker } from '../../../../components/Form/Datepicker/Datepicker';
import { t } from '../../../../locales/translate';
import { TimeRange } from '../../../../model/util';
import { createClearing } from '../../../../model/order';
import { getSimpleDate } from '../../../../util/util';

interface Props {
  orderId: string;
  controller: ModalController<TimeRange>;
}

export function ClearingOrderModal({ controller, orderId }: Props): JSX.Element {
  // Make default timerange span the previous month
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1);
  const endDate = new Date(now.getFullYear(), now.getMonth(), 0);

  const [changes, , model, trackChanges] = useChanges<TimeRange>({
    startDate: controller.model.startDate || startDate,
    endDate: controller.model.endDate || endDate,
  });

  async function handleOnSubmit(): Promise<void> {
    try {
      await createClearing({
        orderId,
        // Sending strings to make sure we have timezone absolute dates
        from: getSimpleDate(model.startDate) as unknown as Date,
        to: getSimpleDate(model.endDate) as unknown as Date,
      });
      controller.close();
    } catch (e) {
      handleError(e);
    }
  }

  return (
    <Form model={model} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: !!changes && form.valid,
        });
        return (
          <>
            <Datepicker
              name="startDate"
              disabledDays={[{ after: now }]}
              onChange={() => trackChanges({ endDate: undefined }, 'endDate')}
              label={t.model.datetime.startDate}
              required
            />
            <Datepicker
              name="endDate"
              disabledDays={[{ before: model.startDate, after: now }]}
              label={t.model.datetime.endDate}
              required
            />
          </>
        );
      }}
    </Form>
  );
}
